import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { DoorClosed, ChevronDown, ChevronUp, CheckCircle2, XCircle } from "lucide-react";

const TYPE_CLS = {
  "HCP statement": "bg-teal-50 text-teal-700 border-teal-200",
  "Scientific evidence record": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Source document": "bg-sky-50 text-sky-700 border-sky-200",
};

function BlockedRow({ it }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-amber-200 rounded-xl shadow-sm" data-testid={`blocked-${it.item_id}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left px-4 py-2.5 flex flex-wrap items-center gap-2">
        <span className="font-mono text-[9px] text-slate-400">{it.item_id}</span>
        <Badge variant="outline" className={`text-[9px] ${TYPE_CLS[it.item_type]}`}>{it.item_type}</Badge>
        <span className="text-[11px] text-slate-700 flex-1 min-w-[200px] leading-snug">{it.label}</span>
        <span className="text-[9px] font-semibold text-amber-700">{it.blocked_reasons.length} gate(s) failed</span>
        {open ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
      </button>
      {open && (
        <div className="px-4 pb-3 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {it.gates.map((g) => (
            <p key={g.id} className="text-[10.5px] flex items-start gap-1.5">
              {g.passed ? <CheckCircle2 className="w-3 h-3 text-teal-500 mt-0.5 shrink-0" /> : <XCircle className="w-3 h-3 text-red-500 mt-0.5 shrink-0" />}
              <span className={g.passed ? "text-slate-500" : "text-red-700"}>
                <span className="font-semibold">{g.name}</span>{!g.passed && <> — {g.reason}</>}
              </span>
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

export function EligibilityGates() {
  const [data, setData] = useState(null);
  const [typeF, setTypeF] = useState("all");
  useEffect(() => { api.get("/eligibility").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="p-6 text-slate-400 text-sm" data-testid="eligibility-loading">Loading eligibility register…</div>;

  const blocked = data.blocked.filter((b) => typeF === "all" || b.item_type === typeF);
  return (
    <div className="space-y-4" data-testid="eligibility-gates">
      <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm">
        <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-1.5 flex items-center gap-1.5"><DoorClosed className="w-3.5 h-3.5 text-teal-600" />Common eligibility rule · applies to {data.applies_to.join(" · ")}</p>
        <p className="text-[11.5px] text-slate-600 leading-relaxed">{data.policy}</p>
        <div className="flex flex-wrap gap-1.5 mt-2.5">
          {data.gates.map((g, i) => (
            <span key={g.id} className="text-[10px] text-slate-600 bg-slate-50 border border-slate-200 rounded-full px-2.5 py-1"><span className="font-mono text-[9px] text-teal-700 mr-1">G{i + 1}</span>{g.name}</span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="eligibility-tiles">
        {[["Items under the rule", data.summary.total, "text-slate-800"],
          ["Eligible for approved outputs", data.summary.eligible, "text-teal-700"],
          ["Blocked", data.summary.blocked, "text-amber-600"],
          ["Blocked HCP statements", data.summary.by_type["HCP statement"]?.blocked ?? 0, "text-amber-600"]].map(([l, n, cls]) => (
          <div key={l} className="bg-white border border-slate-200/80 rounded-xl p-3 shadow-sm">
            <p className="font-mono text-[8.5px] uppercase tracking-widest text-slate-400 leading-tight">{l}</p>
            <p className={`font-heading text-xl font-bold ${cls}`}>{n}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {Object.entries(data.summary.by_type).map(([t, v]) => (
          <div key={t} className="bg-white border border-slate-200/80 rounded-xl p-3 shadow-sm text-[11px] text-slate-600">
            <Badge variant="outline" className={`text-[9px] ${TYPE_CLS[t]}`}>{t}</Badge>
            <p className="mt-1.5"><span className="font-bold text-teal-700">{v.eligible}</span> eligible · <span className="font-bold text-amber-600">{v.blocked}</span> blocked · {v.total} total</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <p className="text-xs font-bold text-slate-800 mr-2">Blocked records — why each is excluded</p>
        {["all", "HCP statement", "Scientific evidence record", "Source document"].map((t) => (
          <button key={t} onClick={() => setTypeF(t)} data-testid={`elig-filter-${t.toLowerCase().replace(/\s+/g, "-")}`}
            className={`text-[10px] font-semibold rounded-full px-2.5 py-1 border ${typeF === t ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-teal-400"}`}>
            {t === "all" ? `All (${data.blocked.length})` : t}
          </button>
        ))}
      </div>
      <div className="space-y-2" data-testid="blocked-register">
        {blocked.map((it) => <BlockedRow key={it.item_id} it={it} />)}
        {blocked.length === 0 && <p className="text-xs text-slate-400 italic">No blocked records of this type.</p>}
      </div>
    </div>
  );
}
